const getOrderByAction = (shipment, actionName, fallbackIndex) => {
    const orders = Array.isArray(shipment && shipment.Orders) ? shipment.Orders : [];
    const matchingOrder = orders.find(order => order && order.actions && Array.isArray(order.actions[actionName]));

    return matchingOrder || orders[fallbackIndex] || null;
};

const parseItems = (order, shipmentDescription) => {
    const descriptions = [order && order.description, shipmentDescription];

    for (let index = 0; index < descriptions.length; index += 1) {
        const description = descriptions[index];

        if (typeof description !== 'string' || description.trim() === '') {
            continue;
        }

        try {
            const parsedItems = JSON.parse(description);
            if (Array.isArray(parsedItems) && parsedItems.length > 0 && parsedItems.every(item => item && typeof item === 'object')) {
                return parsedItems;
            }
        } catch (_) {}
    }

    const actionItems =
        (order && order.actions && Array.isArray(order.actions.pick_up) && order.actions.pick_up) ||
        (order && order.actions && Array.isArray(order.actions.drop_off) && order.actions.drop_off) ||
        [];

    if (actionItems.length > 0 && actionItems.every(item => item && typeof item === 'object')) {
        return actionItems;
    }

    return [{ quantity: 1, detail: '', unit: '', weight: 1, length: 1, width: 1, height: 1, nmfc: '' }];
};

// Items attached to a single order's action (pick_up/drop_off), falling back to the
// order/shipment description JSON when the action list is empty.
const buildStopItems = order => {
    const actions = order && order.actions;
    const actionItems =
        (actions && Array.isArray(actions.pick_up) && actions.pick_up) ||
        (actions && Array.isArray(actions.drop_off) && actions.drop_off) ||
        null;

    if (Array.isArray(actionItems) && actionItems.length > 0 && actionItems.every(item => item && typeof item === 'object')) {
        return actionItems;
    }

    return parseItems(order, null);
};

// A stop is a drop-off even when its items live under actions.pick_up: dispatch multidrop
// encodes drop waypoints as pick_up entries prefixed with "(drop)". Trust actual_action
// first, then fall back to the "(drop)" marker / explicit drop_off list — NOT merely the
// presence of a pick_up array (which drops also have).
const isPickupStop = order => {
    const actions = (order && order.actions) || {};
    const actualAction = String((order && order.actual_action) || '').trim().toLowerCase();

    if (actualAction === 'dropoff' || actualAction === 'drop_off') {
        return false;
    }
    if (actualAction === 'pickup' || actualAction === 'pick_up') {
        return true;
    }
    if (Array.isArray(actions.pick_up)) {
        const firstEntry = String(actions.pick_up[0] || '').toLowerCase();
        return !firstEntry.includes('(drop)');
    }
    return !Array.isArray(actions.drop_off);
};

export const buildSavedDraftResumeState = draftState => {
    const shipment = draftState && draftState.shipment;
    const shipmentType = (shipment && shipment.shipment_type) || 'direct';
    const isMultidrop = shipmentType === 'multidrop_direct' || shipmentType === 'routed_direct';

    if (!shipment || !shipment.shipment_id || (shipmentType !== 'direct' && !isMultidrop)) {
        return null;
    }

    const calculated = shipment.shipping_fee_blob && shipment.shipping_fee_blob.calculated;
    const priceRange = calculated && calculated.shipping_fee_range;
    const priceCandidate = shipment.quote != null ? shipment.quote : shipment.shipping_fee;
    const price = Number(priceCandidate);

    const common = {
        shipmentId: shipment.shipment_id,
        merchantOrderId: shipment.merchant_order_id || '',
        vehicleType: shipment.vehicle_type || '',
        numPeople: shipment.num_of_gophrs || 1,
        weight: shipment.weight || 0,
        isScheduled: Boolean(shipment.scheduled_for),
        scheduledFor: shipment.scheduled_for || null,
        isExpedited: Boolean(shipment.is_expedited),
        isSuperExpedited: Boolean(shipment.is_super_expedited),
        isShipTomorrow: Boolean(shipment.is_ship_tomorrow),
        distance: shipment.distance || '',
        price: Number.isFinite(price) ? price : null,
        priceRangeLow: priceRange && Number.isFinite(Number(priceRange.min)) ? Number(priceRange.min) : null,
        priceRangeHigh: priceRange && Number.isFinite(Number(priceRange.max)) ? Number(priceRange.max) : null,
        route: shipment.billed_route && typeof shipment.billed_route === 'object' ? shipment.billed_route : {},
        containsAlcohol: Boolean(shipment.contains_alcohol),
        isForkLift: Boolean(shipment.is_fork_lift),
        isDolly: Boolean(shipment.is_dolly),
        isHazmat: Boolean(shipment.is_hazmat),
        isPalletJack: Boolean(shipment.is_pallet_jack),
        isTwicCard: Boolean(shipment.is_twic_card),
        isLiftgate: Boolean(shipment.is_liftgate),
        isHandLoading: Boolean(shipment.is_hand_loading),
        isWeatherProtection: Boolean(shipment.is_weather_protection),
        isMedical: Boolean(shipment.is_medical),
        isPpe: Boolean(shipment.is_personal_protective_equipment),
    };

    if (isMultidrop) {
        // Preserve the stop sequence the backend assigned (order_friendly_id asc), then split into
        // main pickup (first), main dropoff (last), and intermediate waypoints (everything between).
        const orders = (Array.isArray(shipment.Orders) ? shipment.Orders.slice() : []).filter(Boolean);
        orders.sort((a, b) => Number(a.order_friendly_id || 0) - Number(b.order_friendly_id || 0));

        const stops = orders.map(order => {
            return {
                orderId: order.order_id || null,
                isPickup: isPickupStop(order),
                location: (order && order.location_blob) || {},
                items: buildStopItems(order),
                pickupInstruction: (order && order.pick_up_instructions) || '',
                dropoffInstruction: (order && order.drop_off_instructions) || '',
            };
        });

        return { ...common, isMultidrop: true, stops };
    }

    const pickupOrder = getOrderByAction(shipment, 'pick_up', 0);
    const dropoffOrder = getOrderByAction(shipment, 'drop_off', 1);

    return {
        ...common,
        isMultidrop: false,
        pickupOrderId: (pickupOrder && pickupOrder.order_id) || null,
        dropoffOrderId: (dropoffOrder && dropoffOrder.order_id) || null,
        pickup: (pickupOrder && pickupOrder.location_blob) || {},
        dropoff: (dropoffOrder && dropoffOrder.location_blob) || {},
        items: parseItems(pickupOrder || dropoffOrder, shipment.description),
        pickupInstruction: (pickupOrder && pickupOrder.pick_up_instructions) || shipment.pick_up_instructions || '',
        dropoffInstruction: (dropoffOrder && dropoffOrder.drop_off_instructions) || shipment.drop_off_instructions || '',
    };
};

export const buildSavedDraftUpdateRequest = ({ shipmentId, updateUrl, shipmentStatus }) => {
    if (!shipmentId || typeof updateUrl !== 'function') {
        return null;
    }

    return {
        method: 'put',
        url: updateUrl(shipmentId),
        saveAgain: shipmentStatus === 'shipment_saved',
    };
};